// Resolves the Sitecore site name from the browser hostname.
//
// `config.sitecoreSiteName` is baked in at build time and is always the consumer
// site, so it can't be trusted for client-side layout service calls that need to
// be site-accurate (the foodservice and Canadian sites share the same bundle).

import config from '../temp/config';

export const SITE_NAMES = {
  CONSUMER: 'bushs-beans',
  FOODSERVICE: 'bushs-beans-foodservices',
  CANADA: 'bushs-beans-canada',
};

const FOODSERVICE_HOSTS = ['bushbeansfoodservice.com', 'fs.dsbeta.com'];

const matchesHost = (host, domain) => host === domain || host.endsWith(`.${domain}`);

/**
 * @param {string} [hostname] - defaults to the current window hostname
 * @returns {string} the Sitecore site name to send as `sc_site`
 */
export function getSiteNameFromHost(hostname) {
  const host = (
    hostname || (typeof window !== 'undefined' ? window.location.hostname : '')
  ).toLowerCase();

  // No window (SSR) — fall back to whatever the bundle was built for.
  if (!host) return config.sitecoreSiteName || SITE_NAMES.CONSUMER;

  if (FOODSERVICE_HOSTS.some((domain) => matchesHost(host, domain))) {
    return SITE_NAMES.FOODSERVICE;
  }

  if (host.endsWith('.ca')) return SITE_NAMES.CANADA;

  return SITE_NAMES.CONSUMER;
}

/**
 * server.js stamps the resolved site name onto the HTML shell — `<body id>` and the
 * `#root` class — at SSR time. On a 404 there's no `context.site` to read, so it falls
 * back to the build-time site name and the foodservice shell is labelled `bushs-beans`.
 *
 * That matters because most foodservice styling is nested under a
 * `.bushs-beans-foodservices` ancestor (e.g. the blue nav background in GlobalStyles),
 * and `#root` sits outside the React tree — repairing the Sitecore context can't reach
 * it. This re-stamps it the same way the server would have.
 *
 * @param {string} siteName
 */
export function applySiteShellClass(siteName) {
  if (typeof document === 'undefined' || !siteName) return;

  const root = document.getElementById('root');
  // Mirrors server.js exactly: the shell carries the site name and nothing else.
  if (root) root.className = siteName;
  document.body.id = siteName;
}
